import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton, Text } from '@fly/core';
import { DISPLAY_DATE_FORMAT, IThread } from '@fly/shared';
import { Skeleton } from '@radix-ui/themes';
import classNames from 'classnames';
import { formatDate } from 'date-fns';

interface ChatHistoryRowProps {
  thread: IThread;
  isActive: boolean;
  onSelect: (threadId: string) => void;
  onDelete: (threadId: string) => void;
}

export const ChatHistoryRow: React.FC<ChatHistoryRowProps> = ({
  thread,
  isActive,
  onSelect,
  onDelete,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className='group flex items-center justify-between gap-fly-2 rounded-fly-2 py-fly-2'
      data-testid='chat-history-item'
    >
      <Button
        size='2'
        variant='ghost'
        color='neutral'
        className={classNames(
          'h-auto grow justify-start truncate',
          isActive && 'not-hover:bg-grayA-2'
        )}
        onClick={() => onSelect(thread.thread_id)}
      >
        <div className='flex flex-col items-start'>
          <span>{thread.name}</span>
          <Text as='span' size='1' color='gray' className='opacity-72'>
            {formatDate(thread.updated_at, DISPLAY_DATE_FORMAT)}
          </Text>
        </div>
      </Button>
      <IconButton
        className={classNames(
          'transition-duration-50 opacity-0 transition-opacity group-hover:opacity-100',
          isActive && 'opacity-100'
        )}
        size='1'
        color='error'
        variant='ghost'
        icon='trash'
        aria-label={t('app.dashboard.chat.history.delete_aria_label', {
          name: thread.name,
        })}
        onClick={() => onDelete(thread.thread_id)}
      />
    </div>
  );
};

export const SKELETON_ROWS = 3;

export const ChatHistoryRowSkeleton: React.FC = () => (
  <div className='flex items-center justify-between gap-fly-2 rounded-fly-2 py-fly-2'>
    <div className='flex grow flex-col gap-fly-1 px-fly-2'>
      <Skeleton className='h-4 w-3/4' />
      <Skeleton className='h-3 w-1/3' />
    </div>
  </div>
);
